import React, { useState, useEffect } from 'react';
import { jobsAPI } from '../../utils/api';
import { fetchAllPages } from '../../utils/fetchAllPages';
import toastr from 'toastr';
import ApplicationView from './ApplicationView';
import InterviewFeedbackDrawer from './InterviewFeedbackDrawer';
import './InterviewScheduleView.css';

/**
 * FIX BL-27: Interview Schedule View for Interviewers
 * Shows upcoming interviews in calendar format
 */
function InterviewScheduleView({ user }) {
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState({
    byStatus: {},
    upcomingThisWeek: 0,
    today: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'upcoming' | 'all' | 'today' | 'this-week' - Default to 'all' to show past interviews
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'scheduled' | 'confirmed' | 'completed'
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showApplicationView, setShowApplicationView] = useState(false);
  const [showFeedbackDrawer, setShowFeedbackDrawer] = useState(false);
  const [feedbackInterview, setFeedbackInterview] = useState(null);

  useEffect(() => {
    fetchInterviews();
    fetchStats();
  }, [filter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Apply date filters
      if (filter === 'today') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        params.startDate = today.toISOString();
        params.endDate = tomorrow.toISOString();
      } else if (filter === 'this-week') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        params.startDate = today.toISOString();
        params.endDate = nextWeek.toISOString();
      } else if (filter === 'upcoming') {
        // Only show future interviews
        const now = new Date();
        params.startDate = now.toISOString();
      }
      // For 'all' filter, don't send date params - backend will return all interviews
      
      // Apply status filter
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Paginated endpoint (50 per page); walk every page so a busy
      // interviewer's full schedule is shown.
      const allInterviews = await fetchAllPages(
        (pageParams) => jobsAPI.getMyInterviews({ ...params, ...pageParams }),
        (data) => data.interviews
      );
      setInterviews(allInterviews);
    } catch (error) {
      console.error('Failed to fetch interviews:', error);
      toastr.error('Failed to load interview schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFeedbackDrawer = (interview, e) => {
    e?.stopPropagation(); // Prevent opening interview details modal
    setFeedbackInterview(interview);
    setShowFeedbackDrawer(true);
  };

  const handleFeedbackSubmitted = () => {
    // Refresh interviews and stats after feedback submission
    fetchInterviews();
    fetchStats();
  };

  const loadInterviewDetails = async (interviewId) => {
    if (!interviewId) return;
    
    try {
      setLoadingDetails(true);
      const response = await jobsAPI.getInterviewDetailsForInterviewer(interviewId);
      setInterviewDetails(response.data);
    } catch (error) {
      console.error('Failed to load interview details:', error);
      toastr.error('Failed to load interview details');
      setInterviewDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleInterviewClick = (interview) => {
    setSelectedInterview(interview);
    loadInterviewDetails(interview._id);
  };

  const handleViewResume = async (interview) => {
    try {
      const res = await jobsAPI.downloadInterviewResume(interview._id);
      if (res.data?.downloadUrl) {
        window.open(res.data.downloadUrl, '_blank');
      } else if (res.data) {
        // Blob fallback — backend streamed the file directly
        const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch {
      toastr.error('Resume not available');
    }
  };

  const isInterviewPast = (interview) => {
    if (!interview || !interview.scheduledDate) return false;
    const interviewEndTime = new Date(new Date(interview.scheduledDate).getTime() + (interview.duration * 60 * 1000));
    return interviewEndTime < new Date();
  };

  const canSubmitFeedback = (interview) => {
    // Can submit feedback if:
    // 1. Interview is in the past (time has passed)
    // 2. Interview status is not cancelled
    // 3. Interview hasn't been completed with feedback already (optional - allow updates)
    if (!interview) return false;
    const past = isInterviewPast(interview);
    const notCancelled = interview.status !== 'cancelled';
    return past && notCancelled;
  };

  const fetchStats = async () => {
    try {
      const response = await jobsAPI.getMyInterviewStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch interview stats:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTime = (dateString) => {
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      scheduled: { bg: '#EFF6FF', color: '#1E40AF', label: 'Scheduled' },
      confirmed: { bg: '#ECFDF5', color: '#065F46', label: 'Confirmed' },
      completed: { bg: '#F0FDF4', color: '#166534', label: 'Completed' },
      cancelled: { bg: '#FEF2F2', color: '#991B1B', label: 'Cancelled' },
      rescheduled: { bg: '#FEF3C7', color: '#92400E', label: 'Rescheduled' },
      'no-show': { bg: '#FEE2E2', color: '#7F1D1D', label: 'No Show' }
    };

    const style = statusStyles[status] || statusStyles.scheduled;

    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}>
        {style.label}
      </span>
    );
  };

  const getInterviewTypeBadge = (type, isGroup) => {
    if (isGroup) {
      return <span className="interview-type-badge group">Group Interview</span>;
    }
    
    const typeLabels = {
      video: 'Video Call',
      phone: 'Phone Call',
      'in-person': 'In-Person',
      technical: 'Technical'
    };

    return <span className="interview-type-badge">{typeLabels[type] || type}</span>;
  };

  const groupInterviewsByDate = (interviews) => {
    const grouped = {};
    interviews.forEach(interview => {
      const date = new Date(interview.scheduledDate).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(interview);
    });

    // Sort by date
    return Object.entries(grouped).sort((a, b) => 
      new Date(a[0]) - new Date(b[0])
    );
  };

  const isToday = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    return today.toDateString() === date.toDateString();
  };

  const isTomorrow = (dateString) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const date = new Date(dateString);
    return tomorrow.toDateString() === date.toDateString();
  };

  const getDateLabel = (dateString) => {
    if (isToday(dateString)) return 'Today';
    if (isTomorrow(dateString)) return 'Tomorrow';
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="interview-schedule-loading">
        <div className="loading-spinner"></div>
        <p>Loading your interview schedule...</p>
      </div>
    );
  }

  const groupedInterviews = groupInterviewsByDate(interviews);

  return (
    <div className="interview-schedule-view">
      {/* Stats Header */}
      <div className="schedule-stats">
        <div className="stat-card primary">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{stats.today}</div>
            <div className="stat-label">Today</div>
          </div>
        </div>
        <div className="stat-card secondary">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{stats.upcomingThisWeek}</div>
            <div className="stat-label">This Week</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{stats.byStatus?.completed || 0}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{stats.byStatus?.scheduled || 0}</div>
            <div className="stat-label">Scheduled</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="schedule-filters">
        <div className="filter-group">
          <label>Time Range:</label>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'today' ? 'active' : ''}`}
              onClick={() => setFilter('today')}
            >
              Today
            </button>
            <button
              className={`filter-btn ${filter === 'this-week' ? 'active' : ''}`}
              onClick={() => setFilter('this-week')}
            >
              This Week
            </button>
            <button
              className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${statusFilter === 'scheduled' ? 'active' : ''}`}
              onClick={() => setStatusFilter('scheduled')}
            >
              Scheduled
            </button>
            <button
              className={`filter-btn ${statusFilter === 'confirmed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('confirmed')}
            >
              Confirmed
            </button>
            <button
              className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('completed')}
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      {/* Interview List */}
      {groupedInterviews.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <h3>No interviews scheduled</h3>
          <p>You don't have any interviews matching the selected filters.</p>
        </div>
      ) : (
        <div className="interview-calendar">
          {groupedInterviews.map(([date, dayInterviews]) => (
            <div key={date} className="calendar-day-section">
              <div className="day-header">
                <h3>{getDateLabel(date)}</h3>
                <span className="interview-count">{dayInterviews.length} interview{dayInterviews.length !== 1 ? 's' : ''}</span>
              </div>
              
              <div className="day-interviews">
                {dayInterviews.map((interview) => (
                  <div 
                    key={interview._id} 
                    className={`interview-card ${isToday(interview.scheduledDate) ? 'today' : ''}`}
                    onClick={() => handleInterviewClick(interview)}
                  >
                    <div className="interview-card-header">
                      <div className="time-info">
                        <span className="time">{formatTime(interview.scheduledDate)}</span>
                        <span className="duration">{interview.duration} min</span>
                      </div>
                      <div className="status-badges">
                        {getStatusBadge(interview.status)}
                        {getInterviewTypeBadge(interview.type, interview.isGroupInterview)}
                        {interview.assessment && interview.assessment.assigned && (
                          <span 
                            className={`assessment-badge ${interview.assessment.completed ? 'completed' : 'assigned'}`}
                            title={interview.assessment.completed ? 'Assessment completed' : 'Assessment assigned'}
                          >
                            {interview.assessment.completed ? '✓ Lab Completed' : '🔬 Lab Assigned'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="interview-card-body">
                      <h4 className="candidate-name">{interview.candidate.name}</h4>
                      {interview.job && (
                        <p className="job-title">{interview.job.title}</p>
                      )}
                      <p className="candidate-email">{interview.candidate.email}</p>
                      
                      {interview.isGroupInterview && interview.interviewers && (
                        <div className="interviewers-list">
                          <span className="label">Panel:</span>
                          <span className="value">
                            {interview.interviewers.map(i => i.name).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="interview-card-footer">
                      {interview.candidate?.resume?.gcsPath && (
                        <button
                          className="resume-link-btn"
                          onClick={(e) => { e.stopPropagation(); handleViewResume(interview); }}
                        >
                          View Resume
                        </button>
                      )}
                      {interview.meetingLink && (
                        <a
                          href={interview.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="meeting-link-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Join Meeting
                        </a>
                      )}
                      {canSubmitFeedback(interview) && (
                        <button
                          className="feedback-btn"
                          onClick={(e) => handleOpenFeedbackDrawer(interview, e)}
                        >
                          {interview.feedback ? 'Update Feedback' : 'Submit Feedback'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Interview Detail Drawer */}
      {selectedInterview && (
        <div 
          className="interview-detail-drawer-overlay" 
          data-open="true"
          onClick={() => {
            setSelectedInterview(null);
            setInterviewDetails(null);
          }}
        >
          <div className="interview-detail-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h2>Interview Details</h2>
              <button className="drawer-close" onClick={() => {
                setSelectedInterview(null);
                setInterviewDetails(null);
              }}>×</button>
            </div>

            <div className="drawer-body">
              <div className="detail-section">
                <h3>Schedule</h3>
                <div className="detail-row">
                  <span className="label">Date & Time:</span>
                  <span className="value">{formatDateTime(selectedInterview.scheduledDate)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Duration:</span>
                  <span className="value">{selectedInterview.duration} minutes</span>
                </div>
                <div className="detail-row">
                  <span className="label">Timezone:</span>
                  <span className="value">{selectedInterview.timeZone || 'UTC'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  {getStatusBadge(selectedInterview.status)}
                </div>
              </div>

              <div className="detail-section">
                <h3>Candidate</h3>
                <div className="detail-row">
                  <span className="label">Name:</span>
                  <span className="value">{selectedInterview.candidate.name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Email:</span>
                  <span className="value">{selectedInterview.candidate.email}</span>
                </div>
                {selectedInterview.candidate.phone && (
                  <div className="detail-row">
                    <span className="label">Phone:</span>
                    <span className="value">{selectedInterview.candidate.phone}</span>
                  </div>
                )}
                {selectedInterview.candidate.resume && (selectedInterview.candidate.resume.gcsPath || selectedInterview.candidate.resume.hasResume) && (
                  <div className="detail-row">
                    <span className="label">Resume:</span>
                    <button
                      onClick={() => handleViewResume(selectedInterview)}
                      className="resume-link-btn"
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
                    >
                      {selectedInterview.candidate.resume.filename || 'View Resume'}
                    </button>
                  </div>
                )}
                {selectedInterview._id && (
                  <div className="detail-row" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
                    <button
                      onClick={() => setShowApplicationView(true)}
                      className="btn btn-primary"
                      style={{
                        padding: '0.625rem 1.25rem',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      View Full Application
                    </button>
                  </div>
                )}
              </div>

              {selectedInterview.job && (
                <div className="detail-section">
                  <h3>Position</h3>
                  <div className="detail-row">
                    <span className="label">Title:</span>
                    <span className="value">{selectedInterview.job.title}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Location:</span>
                    <span className="value">{selectedInterview.job.location}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Type:</span>
                    <span className="value">{selectedInterview.job.type}</span>
                  </div>
                </div>
              )}

              {selectedInterview.interviewers && selectedInterview.interviewers.length > 1 && (
                <div className="detail-section">
                  <h3>Interview Panel</h3>
                  {selectedInterview.interviewers.map((interviewer, idx) => (
                    <div key={idx} className="detail-row">
                      <span className="label">{interviewer.name}:</span>
                      <span className="value">{interviewer.email}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedInterview.recruiter && (
                <div className="detail-section">
                  <h3>Recruiter</h3>
                  <div className="detail-row">
                    <span className="label">Name:</span>
                    <span className="value">{selectedInterview.recruiter.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Email:</span>
                    <span className="value">{selectedInterview.recruiter.email}</span>
                  </div>
                </div>
              )}

              {selectedInterview.notes && (
                <div className="detail-section">
                  <h3>Notes</h3>
                  <p className="notes-text">{selectedInterview.notes}</p>
                </div>
              )}

              {/* Assessment/Lab Details */}
              {loadingDetails ? (
                <div className="detail-section">
                  <p>Loading assessment details...</p>
                </div>
              ) : interviewDetails?.assessment && (
                <div className="detail-section">
                  <h3>Assessment & Lab Progress</h3>
                  {interviewDetails.assessment.labSummary && (
                    <div className="lab-summary" style={{ marginBottom: '1rem' }}>
                      <div className="detail-row">
                        <span className="label">Status:</span>
                        <span className="value">
                          {interviewDetails.assessment.labSummary.completedLabs} / {interviewDetails.assessment.labSummary.totalLabs} Labs Completed
                          ({interviewDetails.assessment.labSummary.progressPercentage}%)
                        </span>
                      </div>
                      {interviewDetails.assessment.overallScore !== null && (
                        <div className="detail-row">
                          <span className="label">Overall Score:</span>
                          <span className="value" style={{ fontWeight: '600', color: interviewDetails.assessment.overallScore >= 80 ? '#10B981' : interviewDetails.assessment.overallScore >= 60 ? '#F59E0B' : '#EF4444' }}>
                            {interviewDetails.assessment.overallScore}%
                            {interviewDetails.assessment.passed !== null && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                                ({interviewDetails.assessment.passed ? 'Passed' : 'Failed'})
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {interviewDetails.assessment.labDetails && interviewDetails.assessment.labDetails.length > 0 && (
                    <div className="lab-details" style={{ marginTop: '1rem' }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--text-secondary, #6B7280)' }}>Lab Details:</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {interviewDetails.assessment.labDetails.map((lab, idx) => (
                          <div key={idx} style={{ 
                            padding: '0.75rem', 
                            background: 'var(--bg-secondary, #F9FAFB)', 
                            borderRadius: '6px',
                            border: '1px solid var(--border-light, #E5E7EB)'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <span style={{ fontWeight: '500' }}>Lab {idx + 1}</span>
                              <span style={{ 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '4px', 
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                background: lab.status === 'completed' ? 'var(--success-100, #D1FAE5)' : lab.status === 'active' || lab.status === 'in_progress' ? 'var(--warning-100, #FEF3C7)' : 'var(--gray-100, #F3F4F6)',
                                color: lab.status === 'completed' ? 'var(--success-700, #047857)' : lab.status === 'active' || lab.status === 'in_progress' ? 'var(--warning-700, #92400E)' : 'var(--gray-700, #374151)'
                              }}>
                                {lab.status === 'completed' ? '✓ Completed' : lab.status === 'active' || lab.status === 'in_progress' ? '⏳ In Progress' : '⏸ Pending'}
                              </span>
                            </div>
                            {lab.status === 'completed' && (
                              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #6B7280)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                {lab.score !== undefined && <span>Score: <strong>{lab.score}</strong></span>}
                                {lab.flagsCorrect !== undefined && lab.flagsSubmitted !== undefined && (
                                  <span>Flags: <strong>{lab.flagsCorrect}/{lab.flagsSubmitted}</strong></span>
                                )}
                                {lab.hintsUsed !== undefined && lab.hintsUsed > 0 && (
                                  <span>Hints: <strong>{lab.hintsUsed}</strong></span>
                                )}
                                {lab.timeSpent && (
                                  <span>Time: <strong>{Math.round(lab.timeSpent / 60)}m</strong></span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recruiter Notes */}
              {interviewDetails?.recruiterNotes && interviewDetails.recruiterNotes.length > 0 && (
                <div className="detail-section">
                  <h3>Recruiter Notes</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {interviewDetails.recruiterNotes.map((note, idx) => (
                      <div key={idx} style={{ 
                        padding: '0.75rem', 
                        background: 'var(--bg-secondary, #F9FAFB)', 
                        borderRadius: '6px',
                        border: '1px solid var(--border-light, #E5E7EB)'
                      }}>
                        <p style={{ margin: 0, marginBottom: '0.5rem', color: 'var(--text-primary, #111827)' }}>{note.note}</p>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6B7280)' }}>
                          {note.addedBy?.name || note.addedBy?.email || 'Unknown'} • {new Date(note.addedAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Internal Comments */}
              {interviewDetails?.internalComments && interviewDetails.internalComments.length > 0 && (
                <div className="detail-section">
                  <h3>Internal Comments</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {interviewDetails.internalComments.map((comment, idx) => (
                      <div key={idx} style={{ 
                        padding: '0.75rem', 
                        background: 'var(--bg-secondary, #F9FAFB)', 
                        borderRadius: '6px',
                        border: '1px solid var(--border-light, #E5E7EB)'
                      }}>
                        <p style={{ margin: 0, marginBottom: '0.5rem', color: 'var(--text-primary, #111827)' }}>{comment.comment}</p>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6B7280)' }}>
                          {comment.addedBy?.name || comment.addedBy?.email || 'Unknown'} • {new Date(comment.addedAt).toLocaleString()}
                          {comment.mentions && comment.mentions.length > 0 && (
                            <span style={{ marginLeft: '0.5rem' }}>
                              • Mentions: {comment.mentions.map(m => m.name || m.email).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline/Activity History */}
              {interviewDetails?.timeline && interviewDetails.timeline.length > 0 && (
                <div className="detail-section">
                  <h3>Activity Timeline</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {interviewDetails.timeline.slice().reverse().map((item, idx) => (
                      <div key={idx} style={{ 
                        padding: '0.75rem', 
                        background: 'var(--bg-secondary, #F9FAFB)', 
                        borderRadius: '6px',
                        border: '1px solid var(--border-light, #E5E7EB)',
                        fontSize: '0.875rem'
                      }}>
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem', color: 'var(--text-primary, #111827)' }}>
                          {item.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        {item.description && (
                          <div style={{ color: 'var(--text-secondary, #6B7280)', marginBottom: '0.25rem' }}>
                            {item.description}
                          </div>
                        )}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6B7280)' }}>
                          {item.performedBy?.name || item.performedBy?.email || 'System'} • {new Date(item.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="drawer-footer">
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {canSubmitFeedback(selectedInterview) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setSelectedInterview(null);
                      handleOpenFeedbackDrawer(selectedInterview);
                    }}
                  >
                    {selectedInterview.feedback ? 'Update Feedback' : 'Submit Feedback'}
                  </button>
                )}
                {selectedInterview.meetingLink && (
                  <a 
                    href={selectedInterview.meetingLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Join Meeting
                  </a>
                )}
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setSelectedInterview(null);
                    setInterviewDetails(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application View Drawer */}
      {selectedInterview && (
        <ApplicationView
          interviewId={selectedInterview._id || selectedInterview.id}
          isOpen={showApplicationView}
          onClose={() => setShowApplicationView(false)}
        />
      )}

      {/* Feedback Drawer */}
      <InterviewFeedbackDrawer
        interview={feedbackInterview}
        isOpen={showFeedbackDrawer}
        onClose={() => {
          setShowFeedbackDrawer(false);
          setFeedbackInterview(null);
        }}
        onSubmitted={handleFeedbackSubmitted}
      />
    </div>
  );
}

export default InterviewScheduleView;


